// Pourquoi un compte synchronisé a-t-il disparu côté broker ?
//
// PROBLÈME (cas réel VIP)
// Quand un trader VALIDE son challenge, la prop firm ne modifie pas le compte :
// elle le CLÔTURE et en crée un nouveau (le compte funded, avec un autre id).
// Côté Edgely, le compte challenge disparaît donc de /account/list au cycle
// suivant, exactement comme un compte CRAMÉ. Le pipeline le désactivait alors
// avec le motif générique `broker_account_gone`, dont le libellé annonce
// « compte cramé ou fermé par la prop firm » : un trader qui vient de RÉUSSIR
// son challenge se voyait afficher un point rouge et un message d'échec.
//
// PRINCIPE
// La disparition seule est ambiguë. Ce qui lève l'ambiguïté, c'est ce qu'Edgely
// sait DÉJÀ du compte au moment où il disparaît :
//   - un compte funded lui a été rattaché (previousAccountId) -> la transition
//     a été observée : preuve directe, la plus forte ;
//   - la target du challenge a été franchie (targetReachedAt) -> le compte est
//     mort en vainqueur, indépendamment de tout rattachement.
// Sans aucun de ces signaux, on retombe sur le motif historique.
//
// Le doute profite au trader UNIQUEMENT sur des faits déjà établis par le
// moteur de règles (targetReachedAt est posé par computeChallengeProgress, pas
// deviné ici) : on n'invente aucune réussite.
//
// Module PUR : aucun import Wasp/Prisma/réseau, testable isolément.

/** Motifs de désactivation automatique, persistés dans Account.deactivatedReason. */
export const CLOSURE_REASON = {
  /** Le compte a disparu du broker sans signal de réussite. */
  BROKER_GONE: "broker_account_gone",
  /** Le compte a franchi sa target : clôture normale d'un challenge validé. */
  CHALLENGE_COMPLETED: "challenge_completed",
  /** Limite de drawdown franchie. */
  DRAWDOWN_BREACH: "drawdown_breach",
} as const;

export type ClosureReason = (typeof CLOSURE_REASON)[keyof typeof CLOSURE_REASON];

export interface ClosureSignals {
  /** Account.targetReachedAt — posé une seule fois au franchissement. */
  targetReachedAt: Date | null;
  /** Un compte funded référence-t-il ce compte via previousAccountId ? */
  hasLinkedFundedAccount: boolean;
  /**
   * Le compte était-il en violation de drawdown à sa disparition ?
   *
   * CONTRE-PREUVE (cas réel : compte breaché ET clôturé dans la même fenêtre).
   * `targetReachedAt` a pu être posé à tort sur un solde dérivé surévalué (les
   * trades pré-connexion manquent au journal). Sans ce signal, un compte cramé
   * portant un marquage erroné était archivé en « challenge validé », et le
   * faux positif devenait définitif.
   *
   * Un compte réellement validé n'est PAS en violation de drawdown au moment où
   * la prop firm le clôture : violation et réussite sont mutuellement exclusives.
   * Quand les deux signaux se contredisent, la violation l'emporte.
   *
   * Optionnel : les appelants qui ne savent pas répondre l'omettent et gardent
   * le comportement historique.
   */
  wasInDrawdownViolation?: boolean;
}

/**
 * Qualifie la disparition d'un compte côté broker.
 *
 * Ne décide QUE du motif : la désactivation elle-même reste inchangée (le
 * compte disparaît réellement du broker dans les deux cas, il doit donc cesser
 * de compter comme actif). Seuls le libellé affiché et la notification émise
 * en dépendent.
 */
export function classifyBrokerClosure(signals: ClosureSignals): ClosureReason {
  // Preuve la plus forte, et la seule OBSERVÉE plutôt que déduite : la prop firm
  // a bel et bien créé le compte funded successeur. Rien ne la contredit.
  if (signals.hasLinkedFundedAccount) return CLOSURE_REASON.CHALLENGE_COMPLETED;

  // Le compte était en violation à sa disparition : il est cramé, quoi que dise
  // un `targetReachedAt` qui a pu être posé à tort. Un compte réellement validé
  // n'est pas simultanément en breach de drawdown.
  if (signals.wasInDrawdownViolation) return CLOSURE_REASON.DRAWDOWN_BREACH;

  const reached = signals.targetReachedAt;
  // Une date invalide (colonne corrompue, parsing raté) ne vaut pas preuve de
  // réussite : on retombe sur le motif neutre plutôt que d'annoncer à tort une
  // validation.
  if (reached instanceof Date && !Number.isNaN(reached.getTime())) {
    return CLOSURE_REASON.CHALLENGE_COMPLETED;
  }

  return CLOSURE_REASON.BROKER_GONE;
}
